import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// The map column of the walk viewer: its narrowest width. The dragged width is a per-user setting
// (user_settings.site_walk_side_width), read from the row's data values and saved through update_settings.
const SIDE_MIN = 280
import PanoramaVideo from "../lib/panorama_video"
import PictureFade from "../lib/picture_fade"
import { WalkPathOverlay, startYaw, horizontalFov, headingAt } from "../lib/walk_path"
import { filmedSecondsAt, indexAtTime, indexNear, videoTimeFor, walkPace, fixShift, fixedVideoTime } from "../lib/site_walk_route"
import { planPointAt, planHeadingAt, nearestOnPlan } from "../lib/plan_geometry"
import { trackAt, videoTimeAt, levelMatrix } from "../lib/level"
import { UPLOAD_LIMIT_BYTES, showSizeLimitModal } from "../lib/size_limit_modal"

// Site Walks row on the web Photos page (Phelipe 2026-09-21). Cards are drawn
// by the server (project_site_walks/_row); this opens a walk in the viewer
// with a freshly signed video link, in one of two ways:
//
//   watch — the plain video player.
//   walk  — step along the route with arrows, like Street View: a tap steps
//           a couple of seconds, holding the forward arrow keeps walking,
//           the bar at the bottom jumps anywhere. A walk filmed with a 360°
//           camera is drawn as a look-around (lib/panorama_video) — drag to
//           turn, scroll to zoom — and the arrows still move you along.
//
// It also uploads a walk from a computer (a 360-camera file): the same
// record → signed PUT → complete flow the phone uses, so the video never
// passes through the app server. Whoever added a walk can remove it.
// Originals are full-quality videos (up to 5 GB — past that a pop-up says how
// to get the limit raised, lib/size_limit_modal), so the player only ever
// loads while the viewer is open.
const STEP_SECONDS = 2
const HOLD_MS = 350
const VIDEO_FILE = /\.(mp4|mov|m4v|webm|insv)$/i
// A 360 camera's raw file (K3-5): the server stitches it into a copy that plays.
const INSTA360_RAW = /\.insv$/i
// While a walk's playback copy is being made, the row asks for itself again
// every so often, for up to 45 minutes, so the card turns playable on its own.
const PREPARING_EVERY_MS = 20000
const PREPARING_FOR_MS = 45 * 60 * 1000
let preparingSince = null
// The player bar (K3-5 phase 1.5): normal speed to start, and the pick is
// kept on this computer. (The walk that looked "extremely fast" - Phelipe
// 2026-09-25 - was a timelapse, a picture every 2 s played 30 a second; the
// server now makes its copy at a walking pace, see frame_seconds in _fill.)
// Arrows while watching jump 5 s. 0.1× is as slow as browsers go (Chrome
// refuses anything under 0.0625×).
const SPEEDS = [0.1, 0.25, 0.5, 0.75, 1, 1.5, 2]
const DEFAULT_SPEED = 1
// A new key, so everyone starts at the new default once (older picks were for the fast copy).
const SPEED_KEY = "site-walk-speed-v4"
const WATCH_SEEK_SECONDS = 5
const FULLSCREEN_WAIT_MS = 700

export default class extends Controller {
  static targets = ["card", "extra", "toggle", "count", "viewer", "columns", "splitter", "name", "meta", "lapseNote", "video", "pano", "fadeCanvas", "stage", "openLink",
    "pathCanvas", "areas", "aiSection", "aiNotes", "removeButton", "confirm", "confirmButton", "error", "notice",
    "watchButton", "walkButton", "backButton", "forwardButton", "hint", "time", "route", "panoButton",
    "bar", "playButton", "playIcon", "pauseIcon", "speedButton", "speedMenu", "muteButton", "soundIcon", "mutedIcon",
    "fullscreenButton", "fullIcon", "exitIcon",
    "uploadModal", "uploadName", "uploadAreas", "dropzone", "fileInput", "chips", "uploadError", "uploadButton",
    "saveViewButton", "viewsCount", "viewsPdf", "viewsEmpty", "viewsList",
    "actionModal", "actionPreview", "actionTitle", "actionError", "actionButton",
    "shareState", "shareModal", "shareOffBox", "shareOnBox", "shareMakeButton", "shareLink", "shareError",
    "stages", "mainLabel", "compareStage", "compareVideo", "comparePano", "compareLabel", "nearbyPanel", "nearbyEmpty", "nearbyList"]
  // standalone: a walk shared by link (site_walks_shared/show) - the viewer on
  // its own page, opening autoOpenUrl straight away, with nothing to close.
  static values = { projectId: Number, projectPhase: String, rowUrl: String, text: Object, settingsUrl: String, sideWidth: Number,
    standalone: Boolean, autoOpenUrl: String, autoOpenMode: String }

  connect() {
    this._applySplit()
    this.expanded = false
    this.walk = null
    this.mode = "watch"
    this.projection = "flat"
    this.panorama = null
    this.file = null
    this._onKey = (event) => this._keydown(event)
    this._onKeyUp = (event) => this._keyup(event)
    document.addEventListener("keydown", this._onKey)
    document.addEventListener("keyup", this._onKeyUp)
    this._onTime = () => this._syncRoute()
    this._onPlayState = () => this._syncPlayButton()
    this._onVolume = () => this._syncMuteButton()
    this._onFullscreen = () => this._syncFullscreenButton()
    this._onCompareSync = () => this._syncCompare()
    this._onDocClick = (event) => {
      if (this.hasSpeedMenuTarget && !this.speedMenuTarget.classList.contains("hidden") && !this.speedButtonTarget.contains(event.target)) this._closeSpeedMenu()
    }
    const video = this.videoTarget
    video.addEventListener("timeupdate", this._onTime)
    video.addEventListener("loadedmetadata", this._onTime)
    for (const type of ["play", "pause", "ended"]) video.addEventListener(type, this._onPlayState)
    video.addEventListener("volumechange", this._onVolume)
    video.addEventListener("seeked", this._onCompareSync)
    video.addEventListener("timeupdate", this._onCompareSync)
    document.addEventListener("fullscreenchange", this._onFullscreen)
    document.addEventListener("click", this._onDocClick)
    this._applySpeed(this._savedSpeed())
    this._watchPreparing()
    this._openFromLink()
  }

  // The line between the video and the map column drags (Phelipe 2026-10-13): the column's width is the
  // --walk-side variable on the grid; it is saved on the person's own settings, so it follows them to any
  // computer and changes nothing for anyone else. A double-click puts the default back. The map and the
  // 360 view re-measure on a window resize, so one is sent while dragging.
  startSplit(event) {
    if (!this.hasColumnsTarget || event.button !== 0) return
    event.preventDefault()
    const rect = this.columnsTarget.getBoundingClientRect()
    const max = Math.max(SIDE_MIN, Math.round(rect.width * 0.6))
    let width = null
    const move = (e) => {
      width = Math.min(max, Math.max(SIDE_MIN, Math.round(rect.right - e.clientX - 8)))
      this.columnsTarget.style.setProperty("--walk-side", `${width}px`)
      this._relayout()
    }
    const stop = () => {
      document.removeEventListener("pointermove", move)
      document.removeEventListener("pointerup", stop)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
      if (width == null) return
      this.sideWidthValue = width
      this._saveSide(width)
      track("Site Walk Viewer Resized", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, site_walk_id: this.walk?.id, side_width: width })
    }
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", stop)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
  }

  resetSplit() {
    if (!this.hasColumnsTarget) return
    this.columnsTarget.style.removeProperty("--walk-side")
    this.sideWidthValue = 0
    this._saveSide(0)
    this._relayout()
  }

  _applySplit() {
    if (!this.hasColumnsTarget) return
    const width = this.sideWidthValue
    if (width >= SIDE_MIN) this.columnsTarget.style.setProperty("--walk-side", `${Math.min(width, Math.round(window.innerWidth * 0.6))}px`)
    else this.columnsTarget.style.removeProperty("--walk-side")
  }

  // Saved like the theme and zoom (theme_controller.js): PUT to the person's update_settings; 0 clears it.
  // The shared (public) viewer has no settings URL, so a visitor's drag lasts only while the page is open.
  _saveSide(width) {
    if (!this.hasSettingsUrlValue || !this.settingsUrlValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.settingsUrlValue, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token, Accept: "application/json" },
      body: JSON.stringify({ setting: { site_walk_side_width: width } })
    }).catch(() => { /* a layout preference: nothing to tell the user */ })
  }

  _relayout() {
    if (this._relayoutRaf) return
    this._relayoutRaf = requestAnimationFrame(() => { this._relayoutRaf = 0; window.dispatchEvent(new Event("resize")) })
  }

  disconnect() {
    clearTimeout(this._prepTimer)
    document.removeEventListener("keydown", this._onKey)
    document.removeEventListener("keyup", this._onKeyUp)
    document.removeEventListener("fullscreenchange", this._onFullscreen)
    document.removeEventListener("click", this._onDocClick)
    const video = this.videoTarget
    video.removeEventListener("timeupdate", this._onTime)
    video.removeEventListener("loadedmetadata", this._onTime)
    for (const type of ["play", "pause", "ended"]) video.removeEventListener(type, this._onPlayState)
    video.removeEventListener("volumechange", this._onVolume)
    video.removeEventListener("seeked", this._onCompareSync)
    video.removeEventListener("timeupdate", this._onCompareSync)
    this.stopCompare()
    this.comparePanorama?.destroy()
    this._leaveFullscreen()
    this._stopVideo()
    this.panorama?.destroy()
  }

  // ── See all / Show less ─────────────────────────────────────────────

  toggleAll() {
    this.expanded = !this.expanded
    this.extraTargets.forEach((card) => card.classList.toggle("hidden", !this.expanded))
    this._labelToggle()
  }

  _labelToggle() {
    if (!this.hasToggleTarget) return
    const extras = this.extraTargets.length
    this.toggleTarget.classList.toggle("hidden", extras === 0)
    this.toggleTarget.textContent = this.expanded
      ? this.textValue.show_less
      : this.textValue.see_all.replace("__COUNT__", this.cardTargets.length)
  }

  // ── Viewer ──────────────────────────────────────────────────────────

  async open(event) {
    const url = event.params.url
    if (!url) return
    this.dispatch("closed", { target: window }) // no map of the last walk while this one loads
    this.stopCompare()
    this.closeNearby()
    this.url = url
    this.walk = null
    this.requestedMode = event.params.mode === "walk" ? "walk" : "watch"
    this.nameTarget.textContent = ""
    this.metaTarget.textContent = ""
    this.areasTarget.replaceChildren()
    if (this.hasAiSectionTarget) this.aiSectionTarget.classList.add("hidden")
    if (this.hasRemoveButtonTarget) this.removeButtonTarget.hidden = true
    this.openLinkTarget.hidden = true
    if (this.hasShareStateTarget) this.shareStateTarget.hidden = true
    if (this.hasViewsListTarget) this.viewsListTarget.replaceChildren()
    this.panoButtonTarget.classList.add("hidden")
    this._error(null)
    this._notice(null)
    this.projection = "flat"
    this.frameSeconds = 0
    this._replayed = false
    this._applyMode("watch")
    this.viewerTarget.classList.remove("hidden")
    this._applySplit()

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.site_walk) throw new Error(data.error || this.textValue.load_failed)
      this._fill(data.site_walk)
      track("Site Walk Viewed", {
        project_id: this.projectIdValue, project_phase: this.projectPhaseValue,
        site_walk_id: data.site_walk.id, mode: this.mode, is_360: Boolean(data.site_walk.is_360),
        timelapse: Boolean(data.site_walk.timelapse), has_ai_notes: Boolean(data.site_walk.ai_notes)
      })
    } catch (error) {
      this._error(error.message || this.textValue.load_failed)
    }
  }

  _fill(walk) {
    this.walk = walk
    this.nameTarget.textContent = walk.name
    // "Filmed May 7, 2:29 PM · Uploaded Sep 23, 9:10 AM · Quentin Baird · 17 s" (K3-6: both dates)
    this.metaTarget.textContent = [walk.dates_line || walk.filmed_on, walk.filmed_by, walk.duration].filter(Boolean).join(" · ")
    // A timelapse (K3-5): the copy shows each picture for frame_seconds, so
    // in Walk mode an arrow steps one picture, not two seconds of video.
    this.frameSeconds = Number(walk.frame_seconds) || 0
    if (this.hasLapseNoteTarget) {
      this.lapseNoteTarget.textContent = walk.timelapse_line || ""
      this.lapseNoteTarget.classList.toggle("hidden", !walk.timelapse_line)
    }
    // Nothing to play yet (K9-4): no 360° view of an empty player either.
    this.projection = walk.is_360 && walk.video_url ? "360" : "flat"
    // Each walk opens looking the way it was walked - the back side of a raw
    // 360° file (startYaw) - once; after that the view stays where it's turned.
    this._faceStart = true
    // The 360° switch only makes sense for a 360° video.
    this.panoButtonTarget.classList.toggle("hidden", !walk.is_360)

    // Always asked for with CORS, so the picture can be read back for "Save
    // this view" as well as drawn in 360°; where the bucket says no, it
    // reloads plain.
    if (walk.video_url) {
      this._loadVideo(walk.video_url, true)
    }
    // The still from the walk fills the player until the video's first frame.
    if (walk.poster_url) this.videoTarget.poster = walk.poster_url
    else this.videoTarget.removeAttribute("poster")
    // The link opens the original file: the full-quality one, whatever plays
    // here. When that is a different file (a raw camera file, an 8K export)
    // it says so - a raw file won't play in a tab either.
    if (walk.download_url || walk.video_url) {
      const original = Boolean(walk.download_url) && walk.download_url !== walk.video_url
      this.openLinkTarget.href = walk.download_url || walk.video_url
      this.openLinkTarget.textContent = original ? this.textValue.download_original : this.textValue.open_video
      this.openLinkTarget.hidden = false
    }

    const areas = walk.areas || []
    if (areas.length === 0) {
      const none = document.createElement("span")
      none.className = "text-[13px] text-gray-500"
      none.textContent = this.textValue.no_areas
      this.areasTarget.append(none)
    } else {
      // An area with a known start time is a button: click it, walk from there.
      const marks = new Map((walk.area_marks || []).map((mark) => [mark.name, mark.at]))
      areas.forEach((area) => {
        const at = marks.get(area)
        const chip = document.createElement(at == null ? "span" : "button")
        chip.className = "inline-flex items-center px-2 py-0.5 rounded-full text-[13px] " +
          (at == null ? "bg-gray-100 text-gray-700" : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer")
        chip.textContent = at == null ? area : `${area} · ${this._clock(at)}`
        if (at != null) {
          chip.type = "button"
          chip.addEventListener("click", () => {
            this._applyMode("walk")
            this.videoTarget.currentTime = at
            this._syncRoute()
          })
        }
        this.areasTarget.append(chip)
      })
    }

    const reading = walk.ai_status === "queued" || walk.ai_status === "reading"
    if (this.hasAiSectionTarget && (walk.ai_notes || reading)) {
      this.aiNotesTarget.textContent = walk.ai_notes || this.textValue.ai_reading
      this.aiSectionTarget.classList.remove("hidden")
    }

    if (this.hasRemoveButtonTarget) this.removeButtonTarget.hidden = !walk.mine
    walk.views = Array.isArray(walk.views) ? walk.views : []
    this._renderViews()
    this._paintShare()
    this._applySpeed(this._savedSpeed())
    this._applyMode(this.requestedMode)
    // "Watch" means play it; the click that opened the viewer allows sound.
    if (this.requestedMode === "watch" && walk.video_url) this._play()
    // A walk whose copy is still being made - a shared link opened early, or
    // while the copy is made again - says so instead of an empty player (K9-4).
    if (!walk.video_url && walk.waiting_note) this._notice(walk.waiting_note)
    // Opened from a link to a moment (an action item made from a view).
    if (this._pendingMoment) {
      const moment = this._pendingMoment
      this._pendingMoment = null
      this._goToMoment(moment)
    }
    // The map beside the video (site_walk_map_controller.js) draws the route
    // when the walk has one - a raw .insv's GPS track - and the saved views on it.
    this.dispatch("opened", { target: window, detail: { walk, video: this.videoTarget } })
    this._dispatchViews()
  }

  // The map beside the video was clicked on the path: walk from that spot,
  // the same as an area chip with a start time.
  jumpTo(event) {
    const at = Number(event.detail?.at)
    if (!Number.isFinite(at)) return
    this._applyMode("walk")
    this.videoTarget.currentTime = at
    this._syncRoute()
    // A saved view's pin on the map also says which way it looked.
    const yaw = Number(event.detail?.yaw)
    if (Number.isFinite(yaw) && event.detail?.yaw != null && this.panorama) this.panorama.lookAt(yaw, Number(event.detail?.pitch) || 0)
  }

  // A 360° video is drawn from the video's frames, which the browser only
  // allows when the video was fetched with CORS - so that has to be decided
  // before the source is set. If S3 refuses, the video reloads plain and the
  // viewer stays flat.
  _loadVideo(src, withCors) {
    const video = this.videoTarget
    const at = video.currentTime || 0
    this._stopVideo()
    if (withCors) {
      video.setAttribute("crossorigin", "anonymous")
      video.addEventListener("error", () => {
        if (video.getAttribute("crossorigin") !== "anonymous") return
        if (this.projection === "360") this._dropPanorama(this.textValue.pano_failed)
        this._loadVideo(src, false)
      }, { once: true })
    } else {
      video.removeAttribute("crossorigin")
    }
    video.src = src
    if (at > 0) video.addEventListener("loadedmetadata", () => { video.currentTime = at }, { once: true })
  }

  setWatch() { this._applyMode("watch"); this._play() }
  setWalk() { this._applyMode("walk") }

  // One player bar for both modes (K3-5 phase 1.5). Watching plays the video
  // through - with the look-around for a 360° walk - and walking steps along
  // it; both have the bar's play, speed, sound and full screen.
  _applyMode(mode) {
    this.mode = mode
    const walking = mode === "walk"
    const active = "bg-brand text-white"
    const idle = "bg-white text-gray-700 hover:bg-gray-50"
    this.watchButtonTarget.className = this.watchButtonTarget.className.replace(active, "").replace(idle, "").trim() + " " + (walking ? idle : active)
    this.walkButtonTarget.className = this.walkButtonTarget.className.replace(active, "").replace(idle, "").trim() + " " + (walking ? active : idle)

    const video = this.videoTarget
    video.removeAttribute("controls")
    // Step back and forward sit either side of Play in the bar (#19), only while walking.
    for (const button of [this.backButtonTarget, this.forwardButtonTarget]) button.classList.toggle("hidden", !walking)
    if (walking) video.pause()
    else this._stopWalking()
    if (this.projection === "360") this._startPanorama()
    else this._stopPanorama()
    this._syncPictureFade()
    const hint = this.projection === "360"
      ? (walking ? this.textValue.hint_360 : this.textValue.hint_watch_360)
      : (walking ? this.textValue.hint_flat : "")
    this.hintTarget.textContent = hint || ""
    this.hintTarget.classList.toggle("hidden", !hint)
    this._syncRoute()
    this._syncPlayButton()
    this._syncMuteButton()
    this._syncFullscreenButton()
  }

  // ── 360° look-around ────────────────────────────────────────────────

  togglePano() {
    this.projection = this.projection === "360" ? "flat" : "360"
    const playing = !this.videoTarget.paused
    if (this.projection === "360" && this.videoTarget.getAttribute("crossorigin") !== "anonymous" && this.walk?.video_url) {
      this._loadVideo(this.walk.video_url, true)
    }
    this._applyMode(this.mode)
    if (playing && this.mode === "watch") this._play()
  }

  _startPanorama() {
    if (!this.hasPanoTarget) return
    this.panorama ||= new PanoramaVideo(this.panoTarget, this.videoTarget, {
      onError: () => this._dropPanorama(this.textValue.pano_failed),
      onFrame: (view) => this._drawPath(view),
      onTap: (x, y) => this._tapPath(x, y),
      onHover: (x, y) => this._hoverPath(x, y)
    })
    if (this._faceStart) {
      // A timelapse glides from picture to picture instead of cutting (#17),
      // and each picture is turned back level and steadied where the camera
      // said how (#6) - set before looking, which counts from the start.
      this.panorama.setPictures({ frameSeconds: this.frameSeconds, backward: Boolean(this.walk?.filmed_backward) })
      this.panorama.setLevel(levelFor(this.walk))
      // Looking the way they walked, by the same answer the map's fan uses (K10-5).
      this.panorama.lookAt(this._frontBackwardAt(0) ? 180 : 0, 0)
      this._faceStart = false
    }
    this.panoTarget.classList.remove("hidden")
    if (this.hasPathCanvasTarget) this.pathCanvasTarget.classList.remove("hidden")
    this._syncPanoButton(true)
    if (!this.panorama.start()) this._dropPanorama(this.textValue.pano_failed)
  }

  _stopPanorama() {
    this.panorama?.stop()
    if (this.hasPanoTarget) this.panoTarget.classList.add("hidden")
    this.pathOverlay?.clear()
    this._reportFacing(null)
    if (this.hasPathCanvasTarget) this.pathCanvasTarget.classList.add("hidden")
    this._syncPanoButton(false)
  }

  // ── The path on the ground (Phelipe 2026-09-25) ─────────────────────────
  // A raw .insv's GPS track, drawn ahead of the camera in the 360° view as a
  // light pink line (lib/walk_path.js), in step with every frame; a click on
  // it walks there. Walks without a route draw nothing.

  _drawPath(view) {
    if (!this.hasPathCanvasTarget) return
    this.pathOverlay ||= new WalkPathOverlay(this.pathCanvasTarget)
    const route = this.walk?.route
    // The saved views of this moment, pinned where they looked (#8).
    const pins = this.projection === "360" ? this._nearbyPins() : []
    if (!Array.isArray(route) || route.length < 2 || this.projection !== "360") {
      if (pins.length) {
        this.pathOverlay.prepare(view)
        this.pathOverlay.drawPins(pins, view)
      } else if (this.pathOverlay.drawn.length || this.pathOverlay.drawnBehind.length || this.pathOverlay.pins.length) {
        this.pathOverlay.clear()
      }
      // No GPS: the floor plan's fan still turns with the view (#9).
      this._reportFacing(this.projection === "360" ? view : null)
      this._compareLook(view)
      return
    }
    const index = indexAtTime(route, this._routeSeconds())
    const bearing = this._frontBearing()
    this.pathOverlay.draw(route, index, view, {
      backward: this._frontBackwardAt(), facing: bearing == null ? null : (bearing * Math.PI) / 180
    })
    this.pathOverlay.drawPins(pins, view)
    this._reportFacing(view)
    this._compareLook(view)
  }

  // The map beside the video turns a fan the way the 360° view looks (#5,
  // like DroneDeploy's view cone): the way they walked there, turned by how
  // far the view is dragged round. Sent only when it moves a degree or more;
  // null when there's no look-around to follow.
  _reportFacing(view) {
    const last = this._facing
    if (!view) {
      if (last) {
        this._facing = null
        this.dispatch("facing", { target: window, detail: null })
      }
      return
    }
    // bearing: compass degrees, only with a GPS heading (or the camera's own
    // record of where it faced, #6); yaw and startYaw let a floor plan turn
    // its own fan (site_walk_plan_controller).
    // The middle of the picture's bearing (_frontBearing), turned by how far
    // the view is dragged round (the yaw grows to the left).
    const yaw = (view.yaw * 180) / Math.PI
    const backward = this._frontBackwardAt()
    const heading = this.pathOverlay?.heading
    const front = this._frontBearing() ?? (Number.isFinite(heading) ? (heading * 180) / Math.PI + (backward ? 180 : 0) : null)
    const bearing = front == null ? null : ((((front - yaw) % 360) + 360) % 360)
    const spread = horizontalFov(view.fov, view.width, view.height) || 90
    const apart = (a, b) => (a == null || b == null ? (a == null) !== (b == null) : Math.abs(((a - b + 540) % 360) - 180) >= 1)
    if (last && !apart(bearing, last.bearing) && !apart(yaw, last.yaw) && Math.abs(spread - last.spread) < 1) return
    this._facing = { bearing, yaw, startYaw: backward ? 180 : 0, spread }
    this.dispatch("facing", { target: window, detail: this._facing })
  }

  _tapPath(x, y) {
    const pinned = this.pathOverlay?.hitPin(x, y)
    if (pinned != null) {
      this._openPin(pinned)
      return
    }
    const route = this.walk?.route
    const index = this.pathOverlay?.hit(x, y) ?? -1
    if (!route || index < 0) return
    const at = this._videoTimeForRoute(route[index][0])
    this.jumpTo({ detail: { at } })
    track("Site Walk Path Jumped", {
      project_id: this.projectIdValue, project_phase: this.projectPhaseValue,
      site_walk_id: this.walk?.id, timelapse: Boolean(this.walk?.timelapse)
    })
  }

  // A hand over the line says it can be clicked.
  _hoverPath(x, y) {
    if (!this.hasPanoTarget) return
    const onSomething = (this.pathOverlay?.hit(x, y) ?? -1) >= 0 || this.pathOverlay?.hitPin(x, y) != null
    this.panoTarget.style.cursor = onSomething ? "pointer" : ""
  }

  _pace() {
    return walkPace(this.walk, this.videoTarget.duration)
  }

  // Where along the GPS route the frame on screen was taken: the camera's own
  // record when there is one (#6 - its pictures can be 2.1 s apart, and a few
  // go missing), else reckoned from the timelapse's pace.
  // Then the walk's Fix the dot shifts (K9-1), the same as the map's dot (K10-5:
  // without them the fan and the path line took the way they walked from
  // another stretch of the walk than the dot sat on).
  _routeSeconds(t = this.videoTarget.currentTime || 0) {
    return this._baseRouteSeconds(t) + fixShift(t, this.walk?.route_fixes, (v) => this._baseRouteSeconds(v))
  }

  _baseRouteSeconds(t) {
    const at = trackAt(this.walk?.level_track, t, this.frameSeconds)
    return at?.route ?? filmedSecondsAt(t, this._pace())
  }

  // The moment of this walk's copy for a place on its route.
  _videoTimeForRoute(seconds) {
    const own = (s) => videoTimeAt(this.walk?.level_track, s, this.frameSeconds) ??
      videoTimeFor(s, { ...this._pace(), duration: this.videoTarget.duration })
    return fixedVideoTime(seconds, this.walk?.route_fixes, (v) => this._baseRouteSeconds(v), own)
  }

  // Whether the camera's front lens faced the walker at the picture on screen
  // (K10-4: read from the pictures, so a walker who changes grip is followed);
  // else the walk's own answer.
  _backwardAt(t = this.videoTarget.currentTime || 0) {
    const at = trackAt(this.walk?.level_track, t, this.frameSeconds)?.backward
    return typeof at === "boolean" ? at : Boolean(this.walk?.filmed_backward)
  }

  // "Flip direction" (K10-4, 2026-10-23): the view turns half round, and the
  // map's fan with it (Phelipe: "both need to flip"); the walk opens that way
  // from now on, for everyone.
  async flipDirection(event) {
    if (!this.walk) return
    const button = event?.currentTarget
    if (button) button.disabled = true
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/site_walks/${this.walk.id}/flip`, {
        method: "PATCH", headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || this.textValue.flip_failed)
      const before = this.panorama?.looking
      this.walk.flipped = data.flipped
      if (this.panorama) this.panorama.lookAt((before?.yaw ?? startYaw(this.walk)) + 180, before?.pitch ?? 0)
      this._showToast(this.textValue.flipped)
      track("Site Walk Direction Flipped", {
        project_id: this.projectIdValue, project_phase: this.projectPhaseValue,
        site_walk_id: this.walk.id, flipped: Boolean(data.flipped)
      })
    } catch (error) {
      this._error(error.message || this.textValue.flip_failed)
    } finally {
      if (button) button.disabled = false
    }
  }

  // The compass bearing the middle of the picture on screen faced, as the
  // camera recorded it (#6), or null.
  _recordedBearing(t = this.videoTarget.currentTime || 0) {
    return trackAt(this.walk?.level_track, t, this.frameSeconds)?.bearing ?? null
  }

  // ONE answer for which way the camera was held (K10-5, Larry Oct 20 and 27:
  // the dot right, the fan the wrong way, and Flip turning both so a wrong fan
  // stayed wrong). The view opened by the walk's own answer while the fan and
  // the path line took the camera record's, which always counts the front
  // lens as facing the walker; when the two differed the fan pointed back.
  // Now the view, the fan and the path line all go by this: the pictures'
  // own answer (K10-4) or the walk's, turned round by Flip direction.
  _frontBackwardAt(t = this.videoTarget.currentTime || 0) {
    return this._backwardAt(t) !== Boolean(this.walk?.flipped)
  }

  // The compass bearing (degrees) the middle of the picture faced, from the
  // camera's record turned half round where it takes the grip the other way
  // from _frontBackwardAt; null without a record.
  _frontBearing(t = this.videoTarget.currentTime || 0) {
    const at = trackAt(this.walk?.level_track, t, this.frameSeconds)
    if (!Number.isFinite(at?.bearing)) return null
    const recordedBackward = at.backward !== false // the record takes "as usual" as the front lens facing the walker
    return (at.bearing + (recordedBackward === this._frontBackwardAt(t) ? 0 : 180)) % 360
  }

  // White while the look-around is on, dark otherwise - the text colour
  // swapped too (white on white was invisible before).
  _syncPanoButton(on) {
    const button = this.panoButtonTarget
    for (const cls of ["bg-white", "text-gray-900"]) button.classList.toggle(cls, on)
    for (const cls of ["bg-[#000000]/55", "hover:bg-[#000000]/75", "text-white"]) button.classList.toggle(cls, !on)
    button.setAttribute("aria-pressed", String(on))
  }

  // A timelapse played flat fades from picture to picture too (#17); the 360°
  // view does its own glide, and ordinary video needs neither.
  _syncPictureFade() {
    if (!this.hasFadeCanvasTarget) return
    const on = this.projection !== "360" && this.frameSeconds > 0 && !this.viewerTarget.classList.contains("hidden")
    this.pictureFade ||= new PictureFade(this.videoTarget, this.fadeCanvasTarget)
    if (on) this.pictureFade.start(this.frameSeconds)
    else this.pictureFade.stop()
    this.fadeCanvasTarget.classList.toggle("hidden", !on)
  }

  _dropPanorama(message) {
    this._stopPanorama()
    this.projection = "flat"
    if (message) this._notice(message)
    this._syncPictureFade()
    const hint = this.mode === "walk" ? this.textValue.hint_flat : ""
    this.hintTarget.textContent = hint
    this.hintTarget.classList.toggle("hidden", !hint)
  }

  // ── The player bar ──────────────────────────────────────────────────

  togglePlay() {
    const video = this.videoTarget
    if (video.paused || video.ended) {
      this._play()
    } else {
      video.pause()
      this._syncPlayButton()
    }
  }

  // The button follows the video's own play/pause events; syncing here too
  // covers a play the browser refuses (no click yet) or a player with no events.
  // Chrome may also cut short the first play of a still-loading silent video
  // (a stitched raw file has no sound) as "background media"; one more ask
  // once it can play is enough.
  _play() {
    const video = this.videoTarget
    const promise = video.play()
    this._syncPlayButton()
    promise?.catch?.((error) => {
      this._syncPlayButton()
      if (error?.name !== "AbortError" || this._replayed) return
      this._replayed = true
      const again = () => { if (video.paused && !this.viewerTarget.classList.contains("hidden")) this._play() }
      if (video.readyState >= 3) again()
      else video.addEventListener("canplay", again, { once: true })
    })
  }

  _syncPlayButton() {
    if (!this.hasPlayButtonTarget) return
    const video = this.videoTarget
    const playing = !video.paused && !video.ended
    this.playIconTarget.classList.toggle("hidden", playing)
    this.pauseIconTarget.classList.toggle("hidden", !playing)
    const label = playing ? this.textValue.pause : this.textValue.play
    this.playButtonTarget.setAttribute("aria-label", label)
    this.playButtonTarget.title = label
  }

  toggleMute() {
    this.videoTarget.muted = !this.videoTarget.muted
    this._syncMuteButton()
  }

  _syncMuteButton() {
    if (!this.hasMuteButtonTarget) return
    const muted = this.videoTarget.muted
    this.soundIconTarget.classList.toggle("hidden", muted)
    this.mutedIconTarget.classList.toggle("hidden", !muted)
    const label = muted ? this.textValue.unmute : this.textValue.mute
    this.muteButtonTarget.setAttribute("aria-label", label)
    this.muteButtonTarget.title = label
  }

  toggleSpeedMenu(event) {
    event?.stopPropagation()
    if (!this.hasSpeedMenuTarget) return
    const open = this.speedMenuTarget.classList.contains("hidden")
    if (open) this._paintSpeedMenu()
    this.speedMenuTarget.classList.toggle("hidden", !open)
    this.speedButtonTarget.setAttribute("aria-expanded", String(open))
  }

  pickSpeed(event) {
    const speed = Number(event.params.speed)
    if (!SPEEDS.includes(speed)) return
    this._applySpeed(speed)
    try { localStorage.setItem(SPEED_KEY, String(speed)) } catch { /* private mode: not remembered */ }
    this._closeSpeedMenu()
    track("Site Walk Speed Changed", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, site_walk_id: this.walk?.id, speed, mode: this.mode })
  }

  _closeSpeedMenu() {
    if (!this.hasSpeedMenuTarget) return
    this.speedMenuTarget.classList.add("hidden")
    this.speedButtonTarget.setAttribute("aria-expanded", "false")
  }

  // The current speed reads dark on white; the others white on dark.
  _paintSpeedMenu() {
    const current = this.videoTarget.playbackRate
    this.speedMenuTarget.querySelectorAll("[data-site-walks-speed-param]").forEach((option) => {
      const on = Number(option.dataset.siteWalksSpeedParam) === current
      for (const cls of ["bg-white", "text-gray-900"]) option.classList.toggle(cls, on)
      option.classList.toggle("text-white", !on)
      option.setAttribute("aria-checked", String(on))
    })
  }

  _savedSpeed() {
    try {
      const saved = Number(localStorage.getItem(SPEED_KEY))
      return SPEEDS.includes(saved) ? saved : DEFAULT_SPEED
    } catch {
      return DEFAULT_SPEED
    }
  }

  _applySpeed(speed) {
    const video = this.videoTarget
    video.defaultPlaybackRate = speed
    video.playbackRate = speed
    if (this.hasSpeedButtonTarget) this.speedButtonTarget.textContent = `${speed}×`
  }

  // Full screen for the picture and its bar. Where the browser has no full
  // screen for a page element (iPhone), or doesn't answer (an embedded
  // browser), the stage fills the window instead.
  toggleFullscreen() {
    if (this._maximized || document.fullscreenElement === this.stageTarget) { this._leaveFullscreen(); return }
    const stage = this.stageTarget
    if (document.fullscreenEnabled && typeof stage.requestFullscreen === "function") {
      const fallback = setTimeout(() => { if (document.fullscreenElement !== stage) this._maximize(true) }, FULLSCREEN_WAIT_MS)
      const request = stage.requestFullscreen()
      request?.then?.(() => clearTimeout(fallback), () => { clearTimeout(fallback); this._maximize(true) })
    } else {
      this._maximize(true)
    }
    track("Site Walk Full Screen", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, site_walk_id: this.walk?.id, mode: this.mode, is_360: this.projection === "360" })
  }

  _leaveFullscreen() {
    if (this._maximized) this._maximize(false)
    if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {})
  }

  _maximize(on) {
    this._maximized = on
    const stage = this.stageTarget
    for (const cls of ["relative", "aspect-video", "max-h-[calc(96vh-16rem)]", "rounded-lg"]) stage.classList.toggle(cls, !on)
    for (const cls of ["fixed", "inset-0", "z-[70]"]) stage.classList.toggle(cls, on)
    this._syncFullscreenButton()
  }

  _syncFullscreenButton() {
    if (!this.hasFullscreenButtonTarget) return
    const on = Boolean(this._maximized) || (document.fullscreenElement != null && document.fullscreenElement === this.stageTarget)
    this.fullIconTarget.classList.toggle("hidden", on)
    this.exitIconTarget.classList.toggle("hidden", !on)
    const label = on ? this.textValue.exit_fullscreen : this.textValue.fullscreen
    this.fullscreenButtonTarget.setAttribute("aria-label", label)
    this.fullscreenButtonTarget.title = label
  }

  // ── Walking along the route ─────────────────────────────────────────

  forwardDown(event) {
    event.preventDefault()
    this._pressed = "forward"
    this._holdTimer = setTimeout(() => {
      this._holding = true
      this.videoTarget.play()?.catch?.(() => {})
    }, HOLD_MS)
  }

  forwardUp() {
    if (this._pressed !== "forward") return
    clearTimeout(this._holdTimer)
    if (this._holding) this.videoTarget.pause()
    else this._step(this._stride())
    this._pressed = null
    this._holding = false
  }

  backDown(event) {
    event.preventDefault()
    this._pressed = "back"
    this._holdTimer = setTimeout(() => {
      this._holding = true
      this._backTimer = setInterval(() => this._step(-(this.frameSeconds || 1)), 300)
    }, HOLD_MS)
  }

  backUp() {
    if (this._pressed !== "back") return
    clearTimeout(this._holdTimer)
    clearInterval(this._backTimer)
    if (!this._holding) this._step(-this._stride())
    this._pressed = null
    this._holding = false
  }

  seek(event) {
    const video = this.videoTarget
    video.currentTime = Number(event.target.value)
    this._syncRoute()
  }

  // One step along the route: a couple of seconds of video, or one picture of a timelapse.
  _stride() { return this.frameSeconds || STEP_SECONDS }

  _step(seconds, { keepPlaying = false } = {}) {
    const video = this.videoTarget
    const end = Number.isFinite(video.duration) ? video.duration : Infinity
    if (!keepPlaying) video.pause()
    const now = video.currentTime || 0
    let at = now + seconds
    // Stepping through a timelapse lands in the middle of a picture, never
    // on the seam between two, so the browser shows the one meant.
    if (this.frameSeconds && this.mode === "walk") {
      const hold = this.frameSeconds
      at = (Math.floor(now / hold + 1e-3) + Math.round(seconds / hold)) * hold + hold / 2
    }
    video.currentTime = Math.max(0, Math.min(end, at))
    this._syncRoute()
  }

  _stopWalking() {
    clearTimeout(this._holdTimer)
    clearInterval(this._backTimer)
    this._pressed = null
    this._holding = false
  }

  _syncRoute() {
    if (!this.hasRouteTarget) return
    const video = this.videoTarget
    const duration = Number.isFinite(video.duration) ? video.duration : 0
    this.routeTarget.max = duration || 1
    if (document.activeElement !== this.routeTarget) this.routeTarget.value = video.currentTime || 0
    this.timeTarget.textContent = `${this._clock(video.currentTime || 0)} / ${this._clock(duration)}`
  }

  _clock(seconds) {
    const s = Math.max(0, Math.round(seconds))
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
  }

  _keydown(event) {
    // Something on top already used the key (the site plan pop-up's divider nudges with the arrows).
    if (event.defaultPrevented) return
    const viewerOpen = !this.viewerTarget.classList.contains("hidden")
    if (event.key === "Escape") {
      // The browser leaves its own full screen on Escape; the window-filling
      // kind, then the viewer, close one step at a time.
      if (document.fullscreenElement) return
      const open = (name) => this[`has${name}Target`] && !this[`${name[0].toLowerCase()}${name.slice(1)}Target`].classList.contains("hidden")
      if (open("SpeedMenu")) this._closeSpeedMenu()
      else if (this._maximized) this._maximize(false)
      else if (open("ActionModal")) this.closeActionItem()
      else if (open("ShareModal")) this.closeShare()
      else if (open("Confirm")) this.cancelRemove()
      else if (open("UploadModal")) this.closeUpload()
      else if (viewerOpen) this.hideModal()
      return
    }
    if (!viewerOpen) return
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || "") && document.activeElement !== this.routeTarget) return
    const walking = this.mode === "walk"
    // Walking, up and down step too, the way Street View moves (#19).
    if (walking && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
      event.preventDefault()
      this._step(event.key === "ArrowUp" ? this._stride() : -this._stride())
      return
    }
    if (event.key === "ArrowRight") {
      event.preventDefault()
      if (!walking) this._step(WATCH_SEEK_SECONDS, { keepPlaying: true })
      else if (event.repeat) { this._play(); this._keyWalking = true } else this._step(this._stride())
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this._step(walking ? -this._stride() : -WATCH_SEEK_SECONDS, { keepPlaying: !walking })
    } else if (event.key === " ") {
      event.preventDefault()
      this.togglePlay()
    } else if (event.key === "f" || event.key === "F") {
      event.preventDefault()
      this.toggleFullscreen()
    }
  }

  _keyup(event) {
    if (event.key === "ArrowRight" && this._keyWalking) {
      this._keyWalking = false
      this.videoTarget.pause()
    }
  }

  hideModal() {
    // A shared walk is the whole page: nothing to close.
    if (this.standaloneValue) {
      this._leaveFullscreen()
      this._closeSpeedMenu()
      return
    }
    this._leaveFullscreen()
    this._closeSpeedMenu()
    this.stopCompare()
    this.closeNearby()
    if (this.hasActionModalTarget) this.actionModalTarget.classList.add("hidden")
    if (this.hasShareModalTarget) this.shareModalTarget.classList.add("hidden")
    this._stopWalking()
    this._stopPanorama()
    this.pictureFade?.stop()
    this._stopVideo()
    this.viewerTarget.classList.add("hidden")
    if (this.hasConfirmTarget) this.confirmTarget.classList.add("hidden")
    this.dispatch("closed", { target: window })
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Stop the download too, not just the sound: an original can be gigabytes.
  _stopVideo() {
    if (!this.hasVideoTarget) return
    const video = this.videoTarget
    video.pause()
    if (video.hasAttribute("src")) {
      video.removeAttribute("src")
      video.load()
    }
  }

  _error(message) {
    this.errorTarget.textContent = message || ""
    this.errorTarget.classList.toggle("hidden", !message)
  }

  _notice(message) {
    this.noticeTarget.textContent = message || ""
    this.noticeTarget.classList.toggle("hidden", !message)
  }

  // ── Saved views (Site Walks list #8, #14) ───────────────────────────
  // "Save this view" keeps the moment on screen as a picture - which way the
  // 360° view looks, when in the walk, where on the ground. They're listed
  // beside the video (click one to go back there), pinned in the 360° view
  // and on the map, gathered in a PDF, and an action item can start from one.

  async saveView() {
    if (!this.walk?.views_url || this._savingView) return
    this._savingView = true
    const button = this.hasSaveViewButtonTarget ? this.saveViewButtonTarget : null
    if (button) button.disabled = true
    this._notice(null)
    try {
      const shot = await this._capture()
      this._addView(await this._uploadView(shot, null, "view"))
      this._showToast(this.textValue.view_saved)
    } catch (error) {
      this._notice(error.message || this.textValue.view_failed)
    } finally {
      this._savingView = false
      if (button) button.disabled = false
    }
  }

  // The moment on screen: the picture (a JPEG), when in the walk, which way
  // it looks and where on the ground. The 360° view draws itself into the
  // picture; a flat video's frame is copied as it is.
  async _capture() {
    const video = this.videoTarget
    if (!video || video.readyState < 2) throw new Error(this.textValue.view_not_ready)
    video.pause()
    let picture = null
    let looking = null
    if (this.projection === "360" && this.panorama?.running) {
      picture = this.panorama.snapshot()
      looking = this.panorama.looking
    } else {
      const scale = Math.min(1, 1920 / Math.max(video.videoWidth || 1, video.videoHeight || 1))
      const canvas = document.createElement("canvas")
      canvas.width = Math.max(1, Math.round((video.videoWidth || 1) * scale))
      canvas.height = Math.max(1, Math.round((video.videoHeight || 1) * scale))
      const ctx = canvas.getContext?.("2d")
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        try { picture = canvas.toDataURL("image/jpeg", 0.9) } catch { picture = null } // a video the bucket served without CORS
      }
    }
    if (!picture || !picture.startsWith("data:image/jpeg")) throw new Error(this.textValue.view_failed)
    const at = video.currentTime || 0
    const filmed = this._routeSeconds(at)
    const route = this.walk?.route
    const point = Array.isArray(route) && route.length ? route[indexAtTime(route, filmed)] : null
    return {
      picture, blob: pictureBlob(picture), at_seconds: at, filmed_seconds: filmed,
      yaw: looking?.yaw ?? null, pitch: looking?.pitch ?? null, fov: looking?.fov ?? null,
      bearing: looking ? this._facing?.bearing ?? null : null,
      latitude: point ? point[1] : null, longitude: point ? point[2] : null
    }
  }

  // The walk's own three steps: the record and a signed link, the picture
  // straight to S3, then `complete`.
  async _uploadView(shot, caption, purpose) {
    const created = await this._post(this.walk.views_url, {
      at_seconds: shot.at_seconds, filmed_seconds: shot.filmed_seconds, yaw: shot.yaw, pitch: shot.pitch, fov: shot.fov,
      bearing: shot.bearing, latitude: shot.latitude, longitude: shot.longitude, caption
    }, this.textValue.view_failed)
    const put = await fetch(created.upload.url, {
      method: created.upload.method || "PUT", headers: created.upload.headers || { "Content-Type": "image/jpeg" }, body: shot.blob
    })
    if (!put.ok) throw new Error(this.textValue.view_failed)
    const done = await this._post(`${this.walk.views_url}/${created.view.id}/complete`, { purpose }, this.textValue.view_failed)
    return done.view
  }

  _addView(view) {
    if (!view || !this.walk) return
    this.walk.views = [...(this.walk.views || []).filter((v) => v.id !== view.id), view].sort((a, b) => a.at_seconds - b.at_seconds)
    this._renderViews()
    this._dispatchViews()
  }

  _renderViews() {
    if (!this.hasViewsListTarget) return
    const views = this.walk?.views || []
    this.viewsListTarget.replaceChildren(...views.map((view) => this._viewRow(view)))
    this.viewsEmptyTarget.classList.toggle("hidden", views.length > 0)
    this.viewsCountTarget.textContent = views.length ? `· ${views.length}` : ""
    if (this.hasViewsPdfTarget) {
      this.viewsPdfTarget.hidden = views.length === 0
      this.viewsPdfTarget.href = this.walk?.views_pdf_url || "#"
    }
  }

  _viewRow(view) {
    const row = document.createElement("div")
    row.className = "flex items-start gap-2"
    row.dataset.viewId = view.id
    const label = view.caption || this.textValue.view_at.replace("__TIME__", this._clock(view.at_seconds))
    const thumb = document.createElement("button")
    thumb.type = "button"
    thumb.className = "shrink-0 w-20 h-12 rounded-md overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90"
    thumb.title = this.textValue.view_go
    thumb.setAttribute("aria-label", `${this.textValue.view_go}: ${label}`)
    if (view.image_url) {
      const img = document.createElement("img")
      img.src = view.image_url
      img.alt = ""
      img.loading = "lazy"
      img.className = "w-full h-full object-cover"
      thumb.append(img)
    }
    thumb.addEventListener("click", () => this._goToView(view))
    const text = document.createElement("div")
    text.className = "min-w-0 flex-1"
    const title = document.createElement("button")
    title.type = "button"
    title.className = "block max-w-full truncate text-left text-[13px] font-medium text-gray-900 hover:underline cursor-pointer"
    title.textContent = label
    title.addEventListener("click", () => this._goToView(view))
    const meta = document.createElement("div")
    meta.className = "text-[13px] text-gray-500 truncate"
    meta.textContent = [this._clock(view.at_seconds), view.compass, view.saved_by].filter(Boolean).join(" · ")
    text.append(title, meta)
    if (view.action_item) {
      const link = document.createElement("a")
      link.href = view.action_item.url
      link.target = "_blank"
      link.rel = "noopener"
      link.className = "block max-w-full truncate text-[13px] font-medium text-orange-700 hover:underline"
      link.textContent = `${this.textValue.action_item_badge}: ${view.action_item.title}` +
        (view.action_item.closed ? ` · ${this.textValue.action_item_done}` : "")
      text.append(link)
    }
    row.append(thumb, text)
    if (view.mine) {
      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "shrink-0 px-1 text-base leading-none text-gray-400 hover:text-red-600 cursor-pointer"
      remove.setAttribute("aria-label", this.textValue.view_remove)
      remove.title = this.textValue.view_remove
      remove.textContent = "×"
      remove.addEventListener("click", () => this._removeView(view))
      row.append(remove)
    }
    return row
  }

  // Back to a saved view: that moment, looking the same way.
  _goToView(view) {
    this._applyMode("walk")
    this._goToMoment({ at: view.at_seconds, yaw: view.yaw, pitch: view.pitch, fov: view.fov })
  }

  _goToMoment({ at = null, yaw = null, pitch = null, fov = null } = {}) {
    if (Number.isFinite(at)) this._seekTo(at)
    if (Number.isFinite(yaw) && this.panorama) this.panorama.lookAt(yaw, Number.isFinite(pitch) ? pitch : 0, Number.isFinite(fov) ? fov : null)
  }

  _seekTo(at) {
    const video = this.videoTarget
    const go = () => {
      video.currentTime = Math.max(0, at)
      this._syncRoute()
    }
    if (video.readyState >= 1) go()
    else video.addEventListener("loadedmetadata", go, { once: true })
  }

  async _removeView(view) {
    try {
      const response = await fetch(`${this.walk.views_url}/${view.id}`, { method: "DELETE", headers: this._headers() })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || this.textValue.view_failed)
      this.walk.views = (this.walk.views || []).filter((v) => v.id !== view.id)
      this._renderViews()
      this._dispatchViews()
      this._showToast(this.textValue.view_removed)
    } catch (error) {
      this._notice(error.message)
    }
  }

  // Saved views within a picture or two of now: pins in the 360° view.
  _nearbyPins() {
    const views = this.walk?.views
    if (!views?.length) return []
    const now = this.videoTarget.currentTime || 0
    const near = Math.max(1.5, (this.frameSeconds || 0) * 0.6)
    return views.filter((v) => Number.isFinite(v.yaw) && Math.abs(v.at_seconds - now) <= near)
      .map((v) => ({ id: v.id, yaw: v.yaw, pitch: v.pitch || 0, item: Boolean(v.action_item) }))
  }

  // A pin clicked: its action item opens; a plain saved view says what it is.
  _openPin(id) {
    const view = (this.walk?.views || []).find((v) => v.id === id)
    if (!view) return
    if (view.action_item?.url) {
      window.open(view.action_item.url, "_blank", "noopener")
      return
    }
    const label = view.caption || this.textValue.view_at.replace("__TIME__", this._clock(view.at_seconds))
    this.hintTarget.textContent = label
    this.hintTarget.classList.remove("hidden")
  }

  // The map shows them too (site_walk_map_controller#views).
  _dispatchViews() {
    this.dispatch("views", { target: window, detail: { views: this.walk?.views || [] } })
  }

  // ── Action item from this view (#8) ─────────────────────────────────
  // The picture is taken when the button is pressed, so it's what they were
  // looking at; then they say what needs doing, and the action item form
  // opens in a new tab with the picture in and a link back to this moment.

  async openActionItem() {
    if (!this.walk?.views_url || !this.hasActionModalTarget) return
    this._actionShot = null
    this._actionError(null)
    this._notice(null)
    try {
      this._actionShot = await this._capture()
    } catch (error) {
      this._notice(error.message || this.textValue.view_failed)
      return
    }
    this.actionPreviewTarget.src = this._actionShot.picture
    this.actionTitleTarget.value = ""
    this.actionModalTarget.classList.remove("hidden")
    this.actionTitleTarget.focus()
  }

  closeActionItem() {
    if (this._startingItem || !this.hasActionModalTarget) return
    this.actionModalTarget.classList.add("hidden")
    this._actionShot = null
  }

  async submitActionItem(event) {
    event?.preventDefault?.()
    if (!this._actionShot || this._startingItem) return
    const title = this.actionTitleTarget.value.trim()
    if (!title) {
      this._actionError(this.textValue.action_item_needed)
      return
    }
    // A tab opened now, while the click still counts, isn't blocked; it's
    // pointed at the form once the picture is saved.
    const tab = window.open("", "_blank")
    this._startingItem = true
    this.actionButtonTarget.disabled = true
    try {
      const view = await this._uploadView(this._actionShot, title, "action_item")
      this._addView(view)
      const url = `${this.walk.action_item_url}?site_walk_view_id=${encodeURIComponent(view.id)}`
      track("Site Walk Action Item Started", {
        project_id: this.projectIdValue, project_phase: this.projectPhaseValue, site_walk_id: this.walk.id,
        site_walk_view_id: view.id, is_360: this.projection === "360", has_location: view.latitude != null
      })
      this._startingItem = false
      this.closeActionItem()
      if (tab) {
        tab.opener = null
        tab.location.href = url
      } else {
        window.location.href = url
      }
    } catch (error) {
      tab?.close()
      this._actionError(error.message || this.textValue.view_failed)
    } finally {
      this._startingItem = false
      this.actionButtonTarget.disabled = false
    }
  }

  _actionError(message) {
    if (!this.hasActionErrorTarget) return
    this.actionErrorTarget.textContent = message || ""
    this.actionErrorTarget.classList.toggle("hidden", !message)
  }

  // ── Share this walk (#11) ───────────────────────────────────────────
  // A link anyone can open - no account - to walk it in 360°. Made on first
  // use, copied, and turned off again (the old link stops working).

  openShare() {
    if (!this.walk?.share_url || !this.hasShareModalTarget) return
    this._shareError(null)
    this._paintShare()
    this.shareModalTarget.classList.remove("hidden")
  }

  closeShare() {
    if (this.hasShareModalTarget) this.shareModalTarget.classList.add("hidden")
  }

  _paintShare() {
    const url = absolute(this.walk?.shared_url)
    if (this.hasShareStateTarget) this.shareStateTarget.hidden = !url
    if (!this.hasShareModalTarget) return
    this.shareOffBoxTarget.classList.toggle("hidden", Boolean(url))
    this.shareOnBoxTarget.classList.toggle("hidden", !url)
    this.shareLinkTarget.value = url || ""
  }

  async makeShareLink() {
    if (!this.walk?.share_url) return
    this.shareMakeButtonTarget.disabled = true
    this._shareError(null)
    try {
      const data = await this._post(this.walk.share_url, {}, this.textValue.share_failed)
      this.walk.shared_url = data.url
      this._paintShare()
      this.shareLinkTarget.focus()
    } catch (error) {
      this._shareError(error.message || this.textValue.share_failed)
    } finally {
      this.shareMakeButtonTarget.disabled = false
    }
  }

  async copyShareLink() {
    const url = this.shareLinkTarget.value
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      this.shareLinkTarget.select()
      document.execCommand?.("copy")
    }
    this._showToast(this.textValue.share_copied)
    track("Site Walk Share Link Copied", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, site_walk_id: this.walk?.id })
  }

  selectShareLink() {
    this.shareLinkTarget.select()
  }

  async stopSharing() {
    if (!this.walk?.share_url) return
    this._shareError(null)
    try {
      const response = await fetch(this.walk.share_url, { method: "DELETE", headers: this._headers() })
      if (!response.ok) throw new Error(this.textValue.share_failed)
      this.walk.shared_url = null
      this._paintShare()
      this._showToast(this.textValue.share_stopped)
    } catch (error) {
      this._shareError(error.message || this.textValue.share_failed)
    }
  }

  _shareError(message) {
    if (!this.hasShareErrorTarget) return
    this.shareErrorTarget.textContent = message || ""
    this.shareErrorTarget.classList.toggle("hidden", !message)
  }

  // ── Plans (#9, #16, #18) ────────────────────────────────────────────
  // The "Site plan on the map" pop-up is its own controller (site_plan);
  // this just asks it to open.
  openSitePlan(event) {
    const planId = Number(event?.params?.planId) || this.walk?.site_plan?.id || null
    this.dispatch("open", { target: window, prefix: "site-plan", detail: { mode: "map", planId } })
  }

  // ── Compare two dates at the same spot (#7) / behind the wall (#10) ──
  // The walks that went past the spot on screen - by GPS, or on the same
  // floor plan - newest first. Pick one and it plays beside this one: at the
  // same spot as this walk moves, looking the same way as this view turns.
  // An earlier walk shows what was there before, walls still open.

  async openCompare() {
    if (!this.walk?.nearby_url || !this.hasNearbyPanelTarget) return
    this.videoTarget.pause()
    this.nearbyPanelTarget.classList.remove("hidden")
    this.nearbyListTarget.replaceChildren()
    const spot = this._spot()
    if (!spot) {
      this._nearbyNote(this.textValue.nearby_no_spot)
      return
    }
    this._nearbyNote(this.textValue.nearby_loading)
    try {
      const response = await fetch(`${this.walk.nearby_url}?${new URLSearchParams(spot)}`, { headers: { Accept: "application/json" } })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || this.textValue.compare_failed)
      const walks = data.walks || []
      this._nearbyNote(walks.length ? null : this.textValue.nearby_none)
      this.nearbyListTarget.replaceChildren(...walks.map((hit) => this._nearbyRow(hit)))
    } catch (error) {
      this._nearbyNote(error.message || this.textValue.compare_failed)
    }
  }

  closeNearby() {
    if (this.hasNearbyPanelTarget) this.nearbyPanelTarget.classList.add("hidden")
  }

  _nearbyNote(message) {
    this.nearbyEmptyTarget.textContent = message || ""
    this.nearbyEmptyTarget.classList.toggle("hidden", !message)
  }

  // Where this walk is now: a place on the ground, or on its floor plan.
  _spot() {
    const t = this.videoTarget.currentTime || 0
    const route = this.walk?.route
    if (Array.isArray(route) && route.length >= 2) {
      const point = route[indexAtTime(route, this._routeSeconds(t))]
      return { lat: point[1], lon: point[2] }
    }
    const plan = this.walk?.plan
    if (plan?.site_plan && plan.points?.length >= 2) {
      const [x, y] = planPointAt(plan.points, t)
      return { site_plan_id: plan.site_plan.id, x, y }
    }
    return null
  }

  _nearbyRow(hit) {
    const row = document.createElement("button")
    row.type = "button"
    row.className = "flex items-center justify-between gap-2 w-full px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-left cursor-pointer"
    const text = document.createElement("span")
    text.className = "min-w-0 truncate text-[13px] text-gray-900"
    text.textContent = [hit.filmed_on || hit.day, hit.name].filter(Boolean).join(" · ")
    const meta = document.createElement("span")
    meta.className = `shrink-0 text-[13px] ${hit.earlier ? "text-orange-700" : "text-gray-500"}`
    meta.textContent = [hit.earlier ? this.textValue.nearby_earlier : this.textValue.nearby_later,
      hit.metres != null ? this.textValue.nearby_away.replace("__M__", Math.round(hit.metres)) : null].filter(Boolean).join(" · ")
    row.append(text, meta)
    row.addEventListener("click", () => this._startCompare(hit))
    return row
  }

  async _startCompare(hit) {
    this.stopCompare()
    try {
      const response = await fetch(hit.url, { headers: { Accept: "application/json" } })
      const data = await response.json().catch(() => ({}))
      const other = data.site_walk
      if (!response.ok || !other?.video_url) throw new Error(data.error || this.textValue.compare_failed)
      this.compare = { walk: other, hit, pace: walkPace(other) }
      this.compareLabelTarget.textContent = hit.filmed_on || hit.day || other.name
      this.mainLabelTarget.textContent = this.textValue.compare_this
      this.mainLabelTarget.classList.remove("hidden")
      this.compareStageTarget.classList.remove("hidden")
      this.stagesTarget.classList.add("md:grid-cols-2")
      const video = this.compareVideoTarget
      video.muted = true
      video.setAttribute("crossorigin", "anonymous")
      video.addEventListener("loadedmetadata", () => { video.currentTime = hit.at }, { once: true })
      video.src = other.video_url
      if (other.is_360 && this.projection === "360") {
        this.comparePanorama ||= new PanoramaVideo(this.comparePanoTarget, video, { onError: () => this._dropComparePanorama() })
        this.comparePanorama.setPictures({ frameSeconds: other.frame_seconds, backward: Boolean(other.filmed_backward) })
        this.comparePanorama.setLevel(levelFor(other))
        this.comparePanorama.lookAt(startYaw(other), 0)
        this.comparePanoTarget.classList.remove("hidden")
        if (!this.comparePanorama.start()) this._dropComparePanorama()
      }
      this.closeNearby()
      track("Site Walk Compared", {
        project_id: this.projectIdValue, project_phase: this.projectPhaseValue, site_walk_id: this.walk?.id,
        other_site_walk_id: other.id, frame: hit.frame, earlier: Boolean(hit.earlier)
      })
    } catch (error) {
      this.stopCompare()
      this._nearbyNote(error.message || this.textValue.compare_failed)
    }
  }

  stopCompare() {
    if (!this.hasCompareStageTarget) return
    this.compare = null
    this.comparePanorama?.stop()
    this.comparePanoTarget.classList.add("hidden")
    const video = this.compareVideoTarget
    video.pause()
    if (video.hasAttribute("src")) {
      video.removeAttribute("src")
      video.load()
    }
    this.compareStageTarget.classList.add("hidden")
    this.stagesTarget.classList.remove("md:grid-cols-2")
    this.mainLabelTarget.classList.add("hidden")
  }

  _dropComparePanorama() {
    this.comparePanorama?.stop()
    if (this.hasComparePanoTarget) this.comparePanoTarget.classList.add("hidden")
  }

  // The other walk follows to the same spot as this one moves.
  _syncCompare() {
    if (!this.compare) return
    const now = Date.now()
    if (now - (this._lastCompareSync || 0) < 200) return
    this._lastCompareSync = now
    const at = this._compareTime()
    const video = this.compareVideoTarget
    if (at == null || !Number.isFinite(at) || video.readyState < 1) return
    if (Math.abs((video.currentTime || 0) - at) > 0.4) video.currentTime = at
  }

  _compareTime() {
    const other = this.compare?.walk
    const t = this.videoTarget.currentTime || 0
    const route = this.walk?.route
    if (Array.isArray(route) && route.length >= 2 && Array.isArray(other?.route) && other.route.length >= 2) {
      const point = route[indexAtTime(route, this._routeSeconds(t))]
      const i = indexNear(other.route, point[1], point[2])
      return videoTimeAt(other.level_track, other.route[i][0], this.compare.pace.frameSeconds) ??
        videoTimeFor(other.route[i][0], { ...walkPace(other, this.compareVideoTarget.duration), duration: this.compareVideoTarget.duration })
    }
    const mine = this.walk?.plan
    const theirs = other?.plan
    if (mine?.points && theirs?.points && mine.site_plan?.id === theirs.site_plan?.id) {
      const [x, y] = planPointAt(mine.points, t)
      return nearestOnPlan(theirs.points, x, y)?.seconds ?? null
    }
    return null
  }

  // Both looking the same way: the other view turned to this view's bearing.
  _compareLook(view) {
    if (!this.compare || !this.comparePanorama?.running || !view) return
    const bearing = this._bearingNow(view)
    const heading = this._otherHeading()
    if (bearing == null || heading == null) return
    this.comparePanorama.lookAt(heading + startYaw(this.compare.walk) - bearing, (view.pitch * 180) / Math.PI, view.fov)
  }

  // Which way this view looks: compass degrees with a GPS track, or degrees
  // from the top of the floor plan.
  _bearingNow(view) {
    const yaw = (view.yaw * 180) / Math.PI
    const start = startYaw(this.walk)
    const route = this.walk?.route
    if (Array.isArray(route) && route.length >= 2) {
      const recorded = this._recordedBearing()
      if (recorded != null) return recorded - yaw
      const heading = this.pathOverlay?.heading
      return heading == null ? null : (heading * 180) / Math.PI + start - yaw
    }
    const plan = this.walk?.plan
    if (plan?.points?.length >= 2 && plan.site_plan) {
      const heading = planHeadingAt(plan.points, this.videoTarget.currentTime || 0, plan.site_plan.height / plan.site_plan.width)
      return heading == null ? null : heading + start - yaw
    }
    return null
  }

  // Which way the other walk was going where it is now (kept while it stands still).
  _otherHeading() {
    const compare = this.compare
    const other = compare?.walk
    const t = this.compareVideoTarget.currentTime || 0
    let heading = null
    const recorded = trackAt(other?.level_track, t, compare?.pace?.frameSeconds || 0)
    if (recorded?.bearing != null) {
      // Its camera's own record: where the middle of its picture faced, less the half turn _compareLook adds back.
      compare.heading = recorded.bearing - startYaw(other)
      return compare.heading
    }
    if (Array.isArray(other?.route) && other.route.length >= 2) {
      const seconds = recorded?.route ?? filmedSecondsAt(t, walkPace(other, this.compareVideoTarget?.duration))
      const h = headingAt(other.route, indexAtTime(other.route, seconds))
      heading = h == null ? null : (h * 180) / Math.PI
    } else if (other?.plan?.points?.length >= 2 && other.plan.site_plan) {
      heading = planHeadingAt(other.plan.points, t, other.plan.site_plan.height / other.plan.site_plan.width)
    }
    if (heading != null) compare.heading = heading
    return compare?.heading ?? null
  }

  // ── Opened from a link ──────────────────────────────────────────────
  // A shared walk opens itself; on the Photos page, a link to a moment of a
  // walk (the one an action item made from a view carries) opens it there:
  // /projects/1/photos?site_walk=12&at=192.4&yaw=180&pitch=-5
  _openFromLink() {
    if (this.standaloneValue) {
      if (this.autoOpenUrlValue) this.open({ params: { url: this.autoOpenUrlValue, mode: this.autoOpenModeValue || "walk" } })
      return
    }
    let params
    try {
      params = new URLSearchParams(window.location.search)
    } catch {
      return
    }
    const id = Number(params.get("site_walk"))
    if (!Number.isInteger(id) || id <= 0 || !this.rowUrlValue) return
    const number = (key) => {
      const value = Number(params.get(key))
      return params.has(key) && Number.isFinite(value) ? value : null
    }
    this._pendingMoment = { at: number("at"), yaw: number("yaw"), pitch: number("pitch") }
    this.open({ params: { url: `${this.rowUrlValue}/${id}`, mode: "walk" } })
  }

  // ── Remove ──────────────────────────────────────────────────────────

  askRemove() {
    if (!this.walk?.mine || !this.hasConfirmTarget) return
    this.confirmTarget.classList.remove("hidden")
  }

  cancelRemove() {
    this.confirmTarget.classList.add("hidden")
  }

  async confirmRemove() {
    if (!this.walk || !this.url) return
    const button = this.confirmButtonTarget
    button.disabled = true
    try {
      const response = await fetch(this.url, { method: "DELETE", headers: this._headers() })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || this.textValue.remove_failed)

      this.cardTargets.find((card) => card.dataset.walkId === String(this.walk.id))?.remove()
      this.hideModal()
      this._showToast(this.textValue.removed)
      this._afterRemove()
    } catch (error) {
      this.confirmTarget.classList.add("hidden")
      this._error(error.message || this.textValue.remove_failed)
    } finally {
      button.disabled = false
    }
  }

  _afterRemove() {
    const left = this.cardTargets.length
    if (left === 0) {
      this._refreshRow()
      return
    }
    if (this.hasCountTarget) this.countTarget.textContent = left
    // Keep four cards in view: promote the first hidden one.
    const visible = this.cardTargets.filter((card) => !card.classList.contains("hidden")).length
    if (!this.expanded && visible < 4) {
      const next = this.extraTargets.find((card) => card.classList.contains("hidden"))
      if (next) {
        next.classList.remove("hidden")
        next.setAttribute("data-site-walks-target", "card")
      }
    }
    this._labelToggle()
  }

  // ── Upload a walk ───────────────────────────────────────────────────

  openUpload() {
    this.file = null
    this.fileMeta = null
    this.chipsTarget.replaceChildren()
    this._uploadError(null)
    this.uploadButtonTarget.disabled = true
    this.uploadModalTarget.classList.remove("hidden")
    this.uploadNameTarget.focus()
  }

  closeUpload() {
    if (this._uploading) return
    this.uploadModalTarget.classList.add("hidden")
  }

  pickFile() { this.fileInputTarget.click() }
  fileChosen() { this._takeFile(this.fileInputTarget.files[0]) }

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-blue-400", "bg-blue-50")
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove("border-blue-400", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.dragLeave()
    this._takeFile(event.dataTransfer?.files?.[0])
  }

  _takeFile(file) {
    if (!file) return
    this._uploadError(null)
    if (!(file.type.startsWith("video/") || VIDEO_FILE.test(file.name))) return this._uploadError(this.textValue.file_type)
    if (file.size > UPLOAD_LIMIT_BYTES) {
      return showSizeLimitModal(file, this.textValue.size_limit, { source: "site_walks", project_id: this.projectIdValue, project_phase: this.projectPhaseValue })
    }

    this.file = file
    this.fileMeta = { duration: null, width: null, height: null }
    const raw = INSTA360_RAW.test(file.name)
    this._chip(file.name, "ready", raw ? "360° · Insta360" : null)
    this.uploadButtonTarget.disabled = false
    if (!this.uploadNameTarget.value.trim()) this.uploadNameTarget.value = file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim()
    // A raw camera file is nothing a browser can read: the server stitches it
    // into 360° and fills in its size and length itself.
    if (raw) return

    // The browser can read the length and size of the video before it goes
    // anywhere: that's how a 360° file (twice as wide as tall) is recognised.
    const probe = document.createElement("video")
    probe.preload = "metadata"
    probe.muted = true
    probe.addEventListener("loadedmetadata", () => {
      this.fileMeta = { duration: Math.round(probe.duration || 0) || null, width: probe.videoWidth || null, height: probe.videoHeight || null }
      URL.revokeObjectURL(probe.src)
      if (this.fileMeta.width && this.fileMeta.height && Math.abs(this.fileMeta.width / this.fileMeta.height - 2) < 0.1) {
        this._chip(file.name, "ready", "360°")
      }
    }, { once: true })
    probe.addEventListener("error", () => URL.revokeObjectURL(probe.src), { once: true })
    probe.src = URL.createObjectURL(file)
  }

  async submitUpload() {
    if (!this.file || this._uploading) return
    const name = this.uploadNameTarget.value.trim()
    if (!name) return this._uploadError(this.textValue.name_needed)

    this._uploading = true
    this.uploadButtonTarget.disabled = true
    this._uploadError(null)
    try {
      const created = await this._post(this.rowUrlValue, {
        name, areas: this.uploadAreasTarget.value, filename: this.file.name,
        content_type: this.file.type || (INSTA360_RAW.test(this.file.name) ? "video/x-insv" : "video/mp4"),
        file_size: this.file.size, duration_seconds: this.fileMeta?.duration, width: this.fileMeta?.width, height: this.fileMeta?.height,
        recorded_at: this.file.lastModified ? new Date(this.file.lastModified).toISOString() : null
      })
      await this._putToS3(created.upload, this.file, (percent) => this._chip(this.file.name, "uploading", null, percent))
      await this._post(`${this.rowUrlValue}/${created.site_walk.id}/complete`, {})
      this._chip(this.file.name, "done")
      track("Site Walk Uploaded", { project_id: this.projectIdValue, project_phase: this.projectPhaseValue, site_walk_id: created.site_walk.id })
      this._uploading = false
      this.closeUpload()
      this._showToast(this.textValue.uploaded)
      await this._refreshRow()
    } catch (error) {
      this._uploading = false
      this.uploadButtonTarget.disabled = false
      this._chip(this.file.name, "ready")
      this._uploadError(error.message || this.textValue.upload_failed)
    }
  }

  // XMLHttpRequest, not fetch: only it reports upload progress.
  _putToS3(upload, file, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open(upload.method || "PUT", upload.url)
      Object.entries(upload.headers || {}).forEach(([key, value]) => xhr.setRequestHeader(key, value))
      xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100)) }
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(this.textValue.upload_failed)))
      xhr.onerror = () => reject(new Error(this.textValue.upload_failed))
      xhr.send(file)
    })
  }

  // The chip pattern every upload in the app uses: blue with a spinner while
  // sending, gray with a document icon once done.
  _chip(name, state, badge = null, percent = null) {
    const chip = document.createElement("span")
    const sending = state === "uploading"
    chip.className = `inline-flex items-center gap-2 max-w-full px-2.5 py-1.5 rounded-lg text-[13px] ${sending ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-700"}`
    chip.innerHTML = sending
      ? '<svg class="animate-spin w-4 h-4 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>'
      : '<svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>'
    const label = document.createElement("span")
    label.className = "truncate"
    label.textContent = sending && percent != null ? this.textValue.uploading.replace("__PERCENT__", percent) + " · " + name : name
    chip.append(label)
    if (badge) {
      const tag = document.createElement("span")
      tag.className = "shrink-0 px-1.5 rounded-full bg-blue-100 text-blue-700 font-semibold"
      tag.textContent = badge
      chip.append(tag)
    }
    if (state === "ready") {
      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
      remove.setAttribute("aria-label", "Remove file")
      remove.textContent = "×"
      remove.addEventListener("click", () => {
        this.file = null
        this.fileInputTarget.value = ""
        this.chipsTarget.replaceChildren()
        this.uploadButtonTarget.disabled = true
      })
      chip.append(remove)
    }
    this.chipsTarget.replaceChildren(chip)
  }

  _uploadError(message) {
    this.uploadErrorTarget.textContent = message || ""
    this.uploadErrorTarget.classList.toggle("hidden", !message)
  }

  // Swap just this row for a fresh one from the server (no page reload).
  async _refreshRow() {
    try {
      const response = await fetch(this.rowUrlValue, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error("row")
      const html = await response.text()
      const holder = document.createElement("div")
      holder.innerHTML = html
      const fresh = holder.querySelector("section[data-controller='site-walks']")
      // The fresh row gets its own controller, which keeps watching if need be.
      if (fresh) { this.element.replaceWith(fresh); return }
    } catch {
      // The upload itself succeeded; the row catches up on the next visit.
    }
    this._watchPreparing()
  }

  _watchPreparing() {
    clearTimeout(this._prepTimer)
    if (!this.element.querySelector("[data-preparing]")) { preparingSince = null; return }
    preparingSince ||= Date.now()
    if (Date.now() - preparingSince > PREPARING_FOR_MS) return
    this._prepTimer = setTimeout(() => this._refreshRow(), PREPARING_EVERY_MS)
  }

  async _post(url, body, fallback = this.textValue.upload_failed) {
    const response = await fetch(url, { method: "POST", headers: { ...this._headers(), "Content-Type": "application/json" }, body: JSON.stringify(body) })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || fallback)
    return data
  }

  _headers() {
    return { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content }
  }

  _showToast(message) {
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msg)
    document.body.prepend(el)
  }
}

// For the 360° view (#6): at `seconds` of a walk's copy, the turn that brings
// the picture back level and steady, and the camera's own turn in radians -
// from the camera's record of how it was turned; null for walks without one.
function levelFor(walk) {
  const track = walk?.level_track
  if (!track?.rows?.length && !track?.up?.length) return null
  const frameSeconds = Number(walk.frame_seconds) || 0
  return (seconds) => {
    const at = trackAt(track, seconds, frameSeconds)
    if (!at) return null
    return { matrix: levelMatrix(at.up, at.turn || 0), turn: ((at.turn || 0) * Math.PI) / 180, backward: at.backward }
  }
}

// A link as someone else would open it: the site's own address in front of a path.
function absolute(url) {
  if (!url) return null
  try {
    return new URL(url, window.location.origin).href
  } catch {
    return url
  }
}

// A data: URL picture as the Blob that's sent to S3.
function pictureBlob(dataUrl) {
  const [head, body] = dataUrl.split(",")
  const type = head.match(/data:([^;]+)/)?.[1] || "image/jpeg"
  const bytes = atob(body)
  const out = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) out[i] = bytes.charCodeAt(i)
  return new Blob([out], { type })
}
